import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as AppConstants from '../../../app/AppConstants';

import { Typography, Box, Button, Grid, Checkbox, FormGroup, FormControlLabel } from '@mui/material';
import BackToMenuButton from '../../../components/util/Button/BackToMenuButton';

import UserInfoPanel, { getUserType, getAdminDept, getAdminOffice, getAdminOfficeCentre, getPaymentCounter, getAdminOfficeUrl, getPaymentCounterUrl, getFormARUrl } from '../../../components/base/UserInfoPanel/UserInfoPanel';
import PendingApplicationDialog from '../../../components/base/ApplicationDialog/PendingApplicationDialog';
import ReminderDialog from '../../../components/base/ApplicationDialog/ReminderDialog'
import PhotoUploadPanel from '../../../components/base/PhotoUpload/PhotoUploadPanel';

import slice, { getPendingCount, setApplicationStage } from './UserApplicationSlice';

function UserApplication(props) {
  const appState = useSelector((state)=>state.app);
  const dispatch = useDispatch();

  // Slice for this page
  const sliceState = useSelector((state) => state.userCreate);
  
  useEffect(() => {
    dispatch(slice.actions.resetStore());
    dispatch(slice.actions.dispatchApplicationInput([{
      name: 'netId',
      value: appState.me.netid?.netid
    }]));

    // handle payment callback
    const query = new URLSearchParams(window.location.search);
    const status = query.get('status');
    if (!!status) {
      if (status === AppConstants.OLPP_PAYMENT_MESSAGE.SUCCEED) {
        dispatch(setApplicationStage(AppConstants.APPLICATION_STAGE.OLPP_SUCCEED));
      }
      else if (status === AppConstants.OLPP_PAYMENT_MESSAGE.FAILED) {
        dispatch(setApplicationStage(AppConstants.APPLICATION_STAGE.OLPP_FAILED));
      }
    }
    // check if pending application
    else dispatch(getPendingCount(appState.me.netid?.netid));

    return () => dispatch(slice.actions.resetStore());
  }, []);

  const handleApplicationInput = evt => {
    dispatch(slice.actions.dispatchApplicationInput([{
      name: evt.target.name,
      value: evt.target.value
    }]));
  };

  const handleAckClicked = () => {
    dispatch(slice.actions.setAcked(!sliceState.acked));
  };

  return (
    sliceState.isPendingDialogOpen
    ? <PendingApplicationDialog onClick={() => window.location.href = "/user/application/list"} />
    : <>
      <ReminderDialog
        isOpen={sliceState.isReminderDialogOpen}
        onClose={() => dispatch(slice.actions.setIsReminderDialogOpen(false))}
        officeCentre={getAdminOfficeCentre(appState)}
        officeUrl={getAdminOfficeUrl(appState)}
      />
      <Box maxWidth="md">
        <Typography variant="pageTitle">Apply Card Replacement</Typography>
        <Box mb={2} p={2}>
          <UserInfoPanel />
          <Grid container spacing={2}>
            <Grid item xs={12}><hr /></Grid>
            <Grid item xs={12}>
              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.SELECT_REASON
                && <>
                <SelectReason
                  appState={appState}
                  applicationInput={sliceState.applicationInput}
                  onChange={handleApplicationInput}
                />

                {sliceState.applicationInput.reason != null
                  && <><br />
                  <SelectAdditional
                    appState={appState}
                    applicationInput={sliceState.applicationInput}
                    onChange={handleApplicationInput}
                  />
                </>}

                {sliceState.applicationInput.additionalService === AppConstants.APPLICATION_ADDITIONAL.PHOTO_CHANGED.ID
                  && <><br />
                  <PhotoUploadPanel
                    photo={sliceState.applicationInput?.photo}
                    onChange={handleApplicationInput}
                  />
                </>}

                {sliceState.applicationInput.reason != null
                  && (sliceState.applicationInput.additionalService != null
                  && sliceState.applicationInput.additionalService !== AppConstants.APPLICATION_ADDITIONAL.PHOTO_CHANGED.ID
                  || sliceState.applicationInput.photo != null
                  && sliceState.applicationInput.additionalService === AppConstants.APPLICATION_ADDITIONAL.PHOTO_CHANGED.ID)
                  && <><br />
                  <Acknowledgement appState={appState}
                    applicationInput={sliceState.applicationInput}
                    acked={sliceState.acked}
                    handleAckClicked={handleAckClicked}
                  />
                </>}
              </>}

              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.SELECT_PAYMENT_METHOD
                && <SelectPaymentMethod appState={appState}
                applicationInput={sliceState.applicationInput}
                onChange={handleApplicationInput}
              />}

              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.COMPLETED
                && <span>
                Your application is submitted successfully.
                <br /><br />
                A <b>non-refundable</b> fee of HK$110.00 will be charged for a replacement student identity card.
                <br /><br />
                In order to complete your application, please collect the debit note slip at {
                  !getAdminOfficeUrl(appState) ? getAdminOfficeCentre(appState) : <a href={getAdminOfficeUrl(appState)} target="_blank">{getAdminOfficeCentre(appState)}</a>
                } and settle the fee at the {
                  !getPaymentCounterUrl(appState) ? "Cashier Counter" : <a href={getPaymentCounterUrl(appState)} target="_blank">Cashier Counter</a>
                } of the Finance Office.
                <br /><br />
                <b>Please retain your payment receipt</b> as you will need to present it, along with your HKID card/Passport/Mainland ID Card, which should tally with the record you registered with PolyU, upon collection of your replacement student identity card.
              </span>}

              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.OLPP_SUCCEED
                && <span>
                Your application is submitted and paid successfully. The transaction detail is shown in <a href={'/user/application/list/'}>Application History</a>. Please keep the payment receipt for future reference. You will be notified by email when the application payment is settled.
              </span>}

              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.OLPP_FAILED
                && <span>Your application is submitted successfully but failed to make payment. Please settle the fee in <a href={'/user/application/list/'}>Application History</a>. You will be notified by email when the application payment is settled.</span>}
              
              {sliceState.applicationStage === AppConstants.APPLICATION_STAGE.COMPLETED_CCP
                && <span>
                Your application is submitted successfully.
                {!!sliceState.ccpUrl && <span>
                  <br /><br />
                  Please settle the fee <a href={sliceState.ccpUrl} target="_blank">HERE</a> or in <a href={'/user/application/list/'}>Application History</a>. Please bring along the receipt to the {getAdminOffice(appState)}. Please keep the payment receipt for future reference.
                </span>}
              </span>}
              
              {(sliceState.applicationStage === AppConstants.APPLICATION_STAGE.COMPLETED ||
                sliceState.applicationStage === AppConstants.APPLICATION_STAGE.OLPP_SUCCEED ||
                sliceState.applicationStage === AppConstants.APPLICATION_STAGE.OLPP_FAILED ||
                sliceState.applicationStage === AppConstants.APPLICATION_STAGE.COMPLETED_CCP)
                && <span>
                <br /><br />
                {/* Please note that it will normally take 3 working days to process the replacement card from the date the application payment is settled. You will be notified by email when the replacement card is ready for collection. */}
                {sliceState.applicationInput?.additionalService === AppConstants.APPLICATION_ADDITIONAL.NAME_CHANGED.ID
                && <>
                  <br /><br />
                  For amendment of name, you should also submit {
                    !getFormARUrl(appState) ? "Form AR1" : <a href={getFormARUrl(appState)} target="_blank">Form AR1</a>
                  } to {
                    !getAdminOfficeUrl(appState) ? getAdminOfficeCentre(appState) : <a href={getAdminOfficeUrl(appState)} target="_blank">{getAdminOfficeCentre(appState)}</a>
                  } for amending your name in PolyU’s records.
                </>}
              </span>}
            </Grid>
          </Grid>
        </Box>

        <StageButtons
          stage={sliceState.applicationStage}
          applicationInput={sliceState.applicationInput}
          acked={sliceState.acked}
        />
      </Box>
    </>
  );
}

function Acknowledgement(props) {
  return (
    <>
      <b>Acknowledgement:</b>
      <ol style={{paddingLeft: 15, fontSize: 15}}>
        <li>
          This system is not applicable to students of PolyU award programmes administered by SPEED, Research programmes with intake cohorts before 2018/19 academic year and Outpost Programmes.
        </li>
        <br />
        <li>
          A <b>non-refundable</b> fee of HK$110.00 will be charged for a replacement card. You will be notified by email when the payment for your application is settled. Please save a copy of the payment receipt for your future reference.
        </li>
        <br />
        <li>
          If you submit your application for replacing a lost/damaged card with all required documents and fee settled by 1:00 pm on a working day, you can collect a new card for campus access at the {getAdminOfficeCentre(props.appState)} starting from 5:00 pm on the same day unless receiving further notice from us. Otherwise, you can collect your card starting from 5:00 pm on the following working day. Please check the opening hours of the {getAdminOfficeCentre(props.appState)}{!getAdminOfficeUrl(props.appState) ? "" : <> at <a href={getAdminOfficeUrl(props.appState)} target="_blank">{getAdminOfficeUrl(props.appState)}</a></>}.
        </li>
        <br />
        <li>
          Upon receipt of all required documents and settlement of card replacement fee, normally it will take 3 working days to process the card replacement applications due to amendment of name, change of programme mode/level or change of {getUserType(props.appState)} photo.
        </li>
        <br />
        <li>
          You will be required to present your HKID card/Passport/Mainland ID Card, which should tally with the record you registered with PolyU, when collecting the replacement card in person.
        </li>
        <br />
        <li>
          In case you could not come in person, you may authorise in writing a representative to collect the replacement card on your behalf. Your representative should bring along a copy of your HKID card/Passport/Mainland ID, which should tally with the record you registered with PolyU, for verification purposes. Such copy will be returned to your representative afterwards.
        </li>
        <br />
        <li>
          <b>Your original {getUserType(props.appState)?.toLowerCase()} identity card will be invalidated once the replacement card is issued. If your original {getUserType(props.appState)?.toLowerCase()} identity card is not lost, you should return it to {getAdminDept(props.appState)} when collecting the replacement card. Otherwise, you should immediately return the original {getUserType(props.appState)?.toLowerCase()} identity card to the {getAdminDept(props.appState)} for cancellation should it be found subsequently. It is an offence to possess more than one {getUserType(props.appState)?.toLowerCase()} identity card for the same programme at the same time and you shall be subject to disciplinary action if found committing such an offence.</b>
        </li>
        <br />
        <li>
          The information given in this application will only be used for processing this application.
        </li>
      </ol>
      <FormControlLabel control={
        <Checkbox size="small" name="acknowledgement"
          checked={props.acked}
          onChange={props.handleAckClicked}
        />}
        label={"I have read and understood the above policies and procedures."}
      />
    </>
  )
}

function SelectReason(props) {
  const applicationReasons = (Object).values(AppConstants.APPLICATION_REASON);

  return (
    <>
      I would like to apply for a new {getUserType(props.appState)} Card due to the following reason:
      <FormGroup style={{minHeight: 130}}>
        {applicationReasons.map((option, i) => (
          <FormControlLabel key={i} control={
            <Checkbox size="small" name={option.NAME}
              checked={props.applicationInput.reason === option.ID}
              onChange={() => props.onChange({target: {
                name: 'reason',
                value: option.ID
              }})}
            />}
            label={option.OPTION_NAME}
          />
        ))}
      </FormGroup>
    </>
  );
}

function SelectAdditional(props) {
  const applicationAdditionals = [
    {
      additional: AppConstants.APPLICATION_ADDITIONAL.NONE,
      note: null
    },
    {
      additional: AppConstants.APPLICATION_ADDITIONAL.PHOTO_CHANGED,
      note: null
    },
    {
      additional: AppConstants.APPLICATION_ADDITIONAL.NAME_CHANGED,
      note: <>
        You should also submit {
          !getFormARUrl(props.appState) ? "Form AR1" : <a href={getFormARUrl(props.appState)} target="_blank">Form AR1</a>
        } to {
          !getAdminOfficeUrl(props.appState) ? getAdminOfficeCentre(props.appState) : <a href={getAdminOfficeUrl(props.appState)} target="_blank">{getAdminOfficeCentre(props.appState)}</a>
        } for amending your name in PolyU’s records.
      </>
    },
    {
      additional: AppConstants.APPLICATION_ADDITIONAL.PROG_CHANGED,
      note: "You must seek department's approval for change of programme mode/level before selecting this option."
    },
  ]

  return (
    <>
      {props.applicationInput.reason === AppConstants.APPLICATION_REASON.INTACT.ID
        ? AppConstants.APPLICATION_REASON.INTACT.OPTION_NAME
        : "Additional service"}:
      <FormGroup style={{minHeight: 130}}>
        {applicationAdditionals.map((option, i) => (
          !(props.applicationInput.reason === AppConstants.APPLICATION_REASON.INTACT.ID
          && option.additional.ID === AppConstants.APPLICATION_ADDITIONAL.NONE.ID)
          && !(props.applicationInput.reason !== AppConstants.APPLICATION_REASON.INTACT.ID
          && props.applicationInput.additionalService === AppConstants.APPLICATION_ADDITIONAL.NONE.ID
          && option.additional.ID !== AppConstants.APPLICATION_ADDITIONAL.NONE.ID)
          && <span key={i}>
            {<FormControlLabel key={i} control={
              <Checkbox size="small" name={option.additional.NAME}
                checked={props.applicationInput.additionalService === option.additional.ID}
                onChange={() => props.onChange({target: {
                  name: 'additionalService',
                  value: option.additional.ID
                }})}
              />}
              label={option.additional.NAME}
            />}
            {!!option.note
            && <div style={{fontSize: 13, marginLeft: 28, marginBottom: 5}}>
              {option.note}
            </div>}
          </span>
        ))}
      </FormGroup>
    </>
  );
}

function SelectPaymentMethod(props) {
  const paymentMethodOptions = [
    {
      method: AppConstants.APPLICATION_PAYMENT_METHOD.OLPP,
      note: "You will be redirected to PolyU Online Payment Platform to proceed payment."
    },
    // {
    //   method: AppConstants.APPLICATION_PAYMENT_METHOD.CCP,
    //   note: "PolyU Online Card Payment System will be shown to proceed payment."
    // },
    {
      method: AppConstants.APPLICATION_PAYMENT_METHOD.INPERSON,
      note: <>Please collect the debit note slip at the {
        !getAdminOfficeUrl(props.appState) ? getAdminOfficeCentre(props.appState) : <a href={getAdminOfficeUrl(props.appState)} target="_blank">{getAdminOfficeCentre(props.appState)}</a>
      } and settle the fee at the {
        !getPaymentCounterUrl(props.appState) ? "Cashier Counter" : <a href={getPaymentCounterUrl(props.appState)} target="_blank">Cashier Counter</a>
      } of the Finance Office.</>
    },
  ]

  return (
    <>
      You need to settle a <b>non-refundable</b> fee of HK$110.00 before your student identity card replacement application could be processed. Please select one of the following payment methods and click “Submit”:
      <FormGroup style={{minHeight: 130}}>
        {paymentMethodOptions.map((option, i) => (
          <span key={i}>
            <FormControlLabel control={
              <Checkbox size="small" name={option.method.NAME}
                checked={props.applicationInput.paymentMethod === option.method.ID}
                onChange={() => props.onChange({target: {
                  name: 'paymentMethod',
                  value: option.method.ID
                }})}
              />}
              label={option.method.NAME}
            />
            { !!option.note && <div style={{fontSize: 13, marginLeft: 28, marginBottom: 5}}>{option.note}</div> }
          </span>
        ))}
      </FormGroup>
    </>
  );
}

function StageButtons(props) {
  const dispatch = useDispatch();

  return (
    <Grid container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      {(props.stage === AppConstants.APPLICATION_STAGE.SELECT_REASON ||
        props.stage === AppConstants.APPLICATION_STAGE.COMPLETED ||
        props.stage === AppConstants.APPLICATION_STAGE.OLPP_SUCCEED ||
        props.stage === AppConstants.APPLICATION_STAGE.OLPP_FAILED ||
        props.stage === AppConstants.APPLICATION_STAGE.COMPLETED_CCP)
        && <Grid item><BackToMenuButton /></Grid>}

      {(props.stage === AppConstants.APPLICATION_STAGE.SELECT_PAYMENT_METHOD)
        && <Grid item>
        <Button variant='contained'
          onClick={() => dispatch(setApplicationStage(AppConstants.APPLICATION_STAGE.SELECT_REASON))}
        >
          Back
        </Button>
      </Grid>}

      {props.stage === AppConstants.APPLICATION_STAGE.SELECT_REASON
        && <Grid item>
        <Button variant='contained'
          disabled={ !props.acked }
          onClick={() => dispatch(setApplicationStage(AppConstants.APPLICATION_STAGE.SELECT_PAYMENT_METHOD))}
        >
          Next
        </Button>
      </Grid>}

      {props.stage === AppConstants.APPLICATION_STAGE.SELECT_PAYMENT_METHOD
        && <Grid item>
        <Button variant='contained'
          disabled={ props?.applicationInput?.paymentMethod == null || props?.isLoading }
          onClick={() => dispatch(setApplicationStage(AppConstants.APPLICATION_STAGE.MAKE_PAYMENT))}
        >
          Submit
        </Button>
      </Grid>}
    </Grid>
  )
}

export default UserApplication;